import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Composable for detecting and managing browser zoom levels
 *
 * @returns {Object} Object containing zoom detection state and reset function
 */
const ZOOM_WARNING_DISMISSED_KEY = 'beeboard_zoom_warning_dismissed';
const ZOOM_WARNING_DISMISSED_LEVEL_KEY_LEGACY =
  'beeboard_zoom_warning_dismissed_level';

export function useBrowserZoom() {
  const { locale } = useI18n();
  const zoomLevel = ref(1);
  const isDetectionSupported = ref(true);
  const isGloballyDismissed = ref(false);

  /**
   * Detects current browser zoom level using multiple methods
   */
  function detectZoom() {
    try {
      // Detect browser zoom by comparing window.outerWidth with window.innerWidth
      // This method works for desktop browsers and ignores OS-level scaling better
      // than devicePixelRatio

      // For desktop browsers, outerWidth includes browser chrome (toolbars, etc.)
      // and innerWidth is the viewport. The ratio changes with zoom.
      const outerWidth = window.outerWidth;
      const innerWidth = window.innerWidth;

      if (outerWidth > 0 && innerWidth > 0) {
        // Calculate zoom level from width ratio
        const widthRatio = outerWidth / innerWidth;

        // Only use this if the ratio seems reasonable (between 0.5 and 3)
        if (widthRatio > 0.5 && widthRatio < 3) {
          zoomLevel.value = Math.round(widthRatio * 100) / 100;
        } else {
          // If ratio seems off, try devicePixelRatio as fallback
          zoomLevel.value =
            Math.round((window.devicePixelRatio || 1) * 100) / 100;
        }
      } else {
        // Fallback to devicePixelRatio if window dimensions not available
        zoomLevel.value =
          Math.round((window.devicePixelRatio || 1) * 100) / 100;
      }
    } catch (error) {
      console.warn('Browser zoom detection failed:', error);
      isDetectionSupported.value = false;
      zoomLevel.value = 1;
    }
  }

  /**
   * Check if browser is zoomed (more than 5% difference from 100%)
   */
  const isZoomed = computed(() => {
    return Math.abs(zoomLevel.value - 1) > 0.05;
  });

  /**
   * Get zoom percentage as a rounded integer
   */
  const zoomPercentage = computed(() => {
    return Math.round(zoomLevel.value * 100);
  });

  /**
   * Reset browser zoom to 100%
   * Note: This doesn't actually reset the browser zoom (not possible via JS),
   * but provides instructions to the user
   */
  function resetZoom() {
    // We can't actually reset browser zoom via JavaScript for security reasons
    // But we can detect if it was reset by the user
    const originalZoom = zoomLevel.value;

    // Show instructions to user on how to reset
    // The actual reset must be done by the user via Ctrl+0 or browser menu
    return {
      canAutoReset: false,
      originalZoom: Math.round(originalZoom * 100),
      instructions: {
        windows: 'Press Ctrl+0 to reset zoom',
        mac: 'Press Cmd+0 to reset zoom',
        menu: 'Or use your browser menu: View → Reset Zoom',
      },
    };
  }

  /**
   * Get the keyboard shortcut to reset zoom
   * Returns the appropriate key combination based on platform and user's i18n locale
   */
  function suggestZoomReset() {
    // Detect if user is on Mac
    const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.userAgent);

    // Use the user's actual i18n locale from vue-i18n
    const isGerman = locale.value === 'de';

    if (isMac) {
      return 'Cmd+0';
    } else if (isGerman) {
      return 'Strg+0';
    } else {
      return 'Ctrl+0';
    }
  }

  /**
   * Load the dismissed state from localStorage
   * Also migrates from the old per-zoom-level format if present
   */
  function loadDismissedState() {
    try {
      const stored = localStorage.getItem(ZOOM_WARNING_DISMISSED_KEY);
      if (stored === 'true') {
        isGloballyDismissed.value = true;

        return;
      }

      // Migrate from legacy per-zoom-level storage
      const legacyStored = localStorage.getItem(
        ZOOM_WARNING_DISMISSED_LEVEL_KEY_LEGACY,
      );
      if (legacyStored) {
        // User had dismissed for a specific zoom level - migrate to global
        localStorage.setItem(ZOOM_WARNING_DISMISSED_KEY, 'true');
        localStorage.removeItem(ZOOM_WARNING_DISMISSED_LEVEL_KEY_LEGACY);
        isGloballyDismissed.value = true;

        return;
      }

      isGloballyDismissed.value = false;
    } catch (error) {
      console.warn('Failed to read dismissed state from localStorage:', error);
      isGloballyDismissed.value = false;
    }
  }

  /**
   * Dismiss warning globally for all zoom levels
   */
  function dismissGlobally() {
    try {
      localStorage.setItem(ZOOM_WARNING_DISMISSED_KEY, 'true');
      isGloballyDismissed.value = true;
    } catch (error) {
      console.warn('Failed to save dismissed state to localStorage:', error);
    }
  }

  /**
   * Clear the dismissed state from localStorage
   */
  function clearDismissedState() {
    try {
      localStorage.removeItem(ZOOM_WARNING_DISMISSED_KEY);
      isGloballyDismissed.value = false;
    } catch (error) {
      console.warn('Failed to clear dismissed state from localStorage:', error);
    }
  }

  /**
   * Check if warning should be shown
   * Returns true if zoomed and not globally dismissed
   */
  const shouldShowWarning = computed(() => {
    if (!isZoomed.value || !isDetectionSupported.value) {
      return false;
    }

    // If globally dismissed, never show warning
    if (isGloballyDismissed.value) {
      return false;
    }

    return true;
  });

  // Set up event listeners
  onMounted(() => {
    // Load dismissed state from localStorage
    loadDismissedState();

    // Skip zoom detection if globally dismissed
    if (isGloballyDismissed.value) {
      return;
    }

    // Detect current zoom level
    detectZoom();

    // Listen for resize events (zoom changes trigger resize)
    window.addEventListener('resize', detectZoom);

    // Also listen for visual viewport changes (more accurate for zoom detection)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', detectZoom);
    }
  });

  onBeforeUnmount(() => {
    // Always try to remove listeners (safe even if not added)
    window.removeEventListener('resize', detectZoom);

    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', detectZoom);
    }
  });

  return {
    zoomLevel,
    zoomPercentage,
    isZoomed,
    isDetectionSupported,
    shouldShowWarning,
    isGloballyDismissed,
    detectZoom,
    resetZoom,
    suggestZoomReset,
    dismissGlobally,
    clearDismissedState,
  };
}
